﻿import { Box, Grid } from '@chakra-ui/react'
import { InsuranceParamsType } from 'BrokerTsLib/src/enums'
import React, { useEffect } from 'react'
import { useParams } from 'react-router'

import { stepsStock } from '@/data/steps'

import { ActiveContractTiles } from '@/components/ActiveContractTiles'
import { CustomWaypoint } from '@/components/CustomWaypoint'
import { Loading } from '@/components/Loading'
import { LoadingFailedAlert } from '@/components/LoadingFailedAlert'
import { Recap } from '@/components/Recap'
import { Steps } from '@/components/Steps/Steps'
import { WizzardHeader } from '@/components/WizzardHeader/WizzardHeader'
import { PolicyVersionsContainer } from '@/containers/steps/vehicle/PolicyVersions/PolicyVersionsContainer'
import { QuoteInformation } from '@/containers/steps/vehicle/StepComplete/forms/QuoteInformation'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { MeetingRecordProvider } from '@/context/MeetingRecordProvider'
import OffersAddonProvider from '@/context/OffersAddonProvider'
import { SelectedOffersAddonProvider } from '@/context/SelectedOffersAddonProvider'
import { SelectedQuoteProvider } from '@/context/SelectedQuoteContext'
import { SidebarProvider, useSidebar } from '@/context/SidebarContext'
import { StepsProvider } from '@/context/StepsContext'
import { WizzardHeaderProvider } from '@/context/WizzardHeaderContext'
import { useGetInsurance } from '@/hooks/useInsuranceParams'
import { useGetOffer } from '@/hooks/useOffer'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import { Sidebar } from '@/layouts/components/Sidebar'
import type { PageSection as Section } from '@/layouts/components/Sidebar'
import { useGetActiveContracts } from '@/hooks/useWizard'

const VwfsCompleteContent = ({ offerId }: { offerId: number }) => {
  const { insuranceParamsId } = useInsuranceParams()
  const { setPageSections, pageSections } = useSidebar()

  const { data: offerData, isLoading: isLoadingOffer, error: offerError } = useGetOffer(offerId, {
    enabled: !!offerId,
    gcTime: 0,
  })
  const { data: activeContracts, isLoading: isLoadingContracts } = useGetActiveContracts(insuranceParamsId)

  const currentContract = activeContracts?.find(c => c.insurerOfferId === offerId)
  const others = (activeContracts ?? []).filter(c => c.insurerOfferId !== offerId)

  const offer = offerData?.data

  const { data: dataInsurance } = useGetInsurance(offerId, { enabled: !!offerId })
  const indexaceInsuranceId = dataInsurance?.data?.insuranceId
  const showIndexace = !!(indexaceInsuranceId && dataInsurance?.data?.contractNumber)

  useEffect(() => {
    if (!offer) return

    const array: Section[] = []
    let i = 1

    array.push({
      id: 'recapitulation',
      sectionTitle: 'Rekapitulace pojištění',
      index: i++,
      children: (
        <CustomWaypoint sectionId="recapitulation" cardProps={{ sx: { gap: 16, pb: 16 } }}>
          <Recap id="recapitulation" sx={{ gap: 16, pb: 16 }}>
            <QuoteInformation offer={offer} />
          </Recap>
        </CustomWaypoint>
      ),
    })

    if (showIndexace && indexaceInsuranceId) {
      array.push({
        id: 'indexace',
        sectionTitle: 'Indexace',
        index: i++,
        children: (
          <CustomWaypoint sectionId="indexace">
            <Recap id="indexace">
              <PolicyVersionsContainer insuranceId={indexaceInsuranceId} />
            </Recap>
          </CustomWaypoint>
        ),
      })
    }

    if (others.length > 0) {
      const otherContractsSectionNumber = i
      array.push({
        id: 'otherContracts',
        sectionTitle: 'Další aktivní smlouvy',
        index: i++,
        children: (
          <CustomWaypoint sectionId="otherContracts" cardProps={{ sx: { gap: 8, pb: 8 } }}>
            <div>
              <ActiveContractTiles
                insuranceParamsId={insuranceParamsId!}
                number={otherContractsSectionNumber}
                additionalContracts={others}
              />
            </div>
          </CustomWaypoint>
        ),
      })
    }

    setPageSections(array)
  }, [offer, activeContracts, insuranceParamsId, showIndexace, indexaceInsuranceId])

  if (isLoadingOffer || isLoadingContracts) return <Loading large />
  if (offerError || !offer) return <LoadingFailedAlert />
  if (activeContracts && activeContracts.length > 0 && !currentContract) return <LoadingFailedAlert text='Uvedená smlouva nebyla nalezena.' />

  return (
    <Grid sx={{ gap: 8 }}>
      {pageSections &&
        pageSections.map((x) => <React.Fragment key={x.id}>{x.children}</React.Fragment>)}
    </Grid>
  )
}

export const VwfsStepCompletePage = () => {
  const { isLoadingData, insuranceParamsId, error, insuranceParams } = useInsuranceParams()
  const { offerId } = useParams()

  const paramsType = insuranceParams?.generalParams?.paramsTypeId
  const isStock = paramsType === InsuranceParamsType.StockBefore || paramsType === InsuranceParamsType.StockAfter

  // 2. krok míří na detail aktuálně zobrazené smlouvy (vč. offerId)
  const stockSteps = isStock
    ? [
      stepsStock[0],
      { ...stepsStock[1], path: `/leasing/case/${insuranceParamsId}/steps/complete/${offerId}` },
    ]
    : undefined

  return (
    <StepsProvider steps={stockSteps} currentStepId={isStock ? 2 : 4}>
      <SidebarProvider>
        <WizzardHeaderProvider>
          {isLoadingData ? (
            <Loading large />
          ) : error ? (
            <LoadingFailedAlert />
          ) : (
            <SelectedQuoteProvider>
              <OffersAddonProvider>
                <SelectedOffersAddonProvider>
                  <MeetingRecordProvider insuranceParamsId={insuranceParamsId!}>
                    <PageWrapper
                      steps={<Steps />}
                      header={<WizzardHeader />}
                      sidebar={<Sidebar />}
                      contentProps={{
                        sx: {
                          pt: { base: 0, md: 0 },
                        },
                      }}
                    >
                      <Box sx={{ pt: { base: 0, md: 8 } }}>
                        <VwfsCompleteContent offerId={Number(offerId)} />
                      </Box>
                    </PageWrapper>
                  </MeetingRecordProvider>
                </SelectedOffersAddonProvider>
              </OffersAddonProvider>
            </SelectedQuoteProvider>
          )}
        </WizzardHeaderProvider>
      </SidebarProvider>
    </StepsProvider>
  )
}
